import { useEffect, useState } from "react";
import { FormProvider } from "../../../../../../common/provider/form/Form.provider";
import { CustomButton } from "../../../../../../common/components/CustomButton/CustomButton";
import { Form, Input, Switch, TimePicker } from "antd";
import style from "./style.module.scss";
import { SimpleInput } from "../../../../../../common/components/simpleInput";
import { ShiftingOperationsService } from "../../../../core/_request";
import { HttpStatusCode } from "axios";
import { InputSelect } from "../../../../../../common/components/InputSelect/InputSelect";
import { API_URLs } from "../../../../../../utilities/API_URLs";
import { Loader } from "../../../../../../common/components/Loader/Loader";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import ShiftingStepsEditor from "./ShiftingStepsEditor";
import { handleNumericDecimalInput } from "../../../../../../common/utils/numericInputHandler";
dayjs.extend(customParseFormat);

type ShiftingOperationsFormType = {
  onFinish: () => void;
  initialValue?: any;
};

const ShiftingOperationsForm = ({
  onFinish,
  initialValue,
}: ShiftingOperationsFormType) => {
  const [form] = Form.useForm();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isChecked, setIsChecked] = useState<boolean>(
    initialValue?.isBargeUsed
  );
  const [shiftingSteps, setShiftingSteps] = useState<any[]>([]);

  const [isLoadingSubmit, setIsLoadingSubmit] = useState<boolean>(false);

  useEffect(() => {
    if (initialValue) {
      form.setFieldsValue({
        ...initialValue,
        loadType: initialValue?.loadType
          ? {
              label: initialValue?.loadType?.name,
              value: initialValue?.loadType?.["@id"],
            }
          : null,
        shiftingType: initialValue?.shiftingType
          ? {
              label: initialValue?.shiftingType?.name,
              value: initialValue?.shiftingType?.["@id"],
            }
          : null,
        loadFillingStatus: initialValue?.loadFillingStatus
          ? {
              label: initialValue?.loadFillingStatus?.name,
              value: initialValue?.loadFillingStatus?.["@id"],
            }
          : null,
        bargeMaterial: initialValue.bargeMaterial?.reference,
        endAt: initialValue?.endAt ? dayjs(initialValue.endAt, "YYYY-MM-DD HH:mm") : dayjs(initialValue.createdAt, "DD/MM/YYYY HH:mm:ss").startOf("day"),
      });

      if (initialValue?.shiftingSteps) {
        setShiftingSteps(
          [...initialValue.shiftingSteps]
            .sort((a, b) => a.stepOrder - b.stepOrder)
            .map(({ locationLabel }, i, arr) => ({
              stepOrder: i + 1,
              locationLabel,
              isFinalPosition: i === arr.length - 1,
            }))
        );
      }
    }
  }, [initialValue]);

  const onSubmitHandler = async (values: any) => {
    try {
      setIsLoadingSubmit(true);
      const dataToPost = {
        ...values,
        ...(initialValue
          ? {
              "@id": initialValue?.["@id"],
            }
          : {}),
        loadType: values?.loadType?.value,
        shiftingType: values?.shiftingType?.value,
        loadFillingStatus: values?.loadFillingStatus?.value,
        tonnage: values?.tonnage.toString(),
        isBargeUsed: isChecked,
        ...(isChecked && { bargeMaterial: values?.bargeMaterial?.value }),
        shiftingSteps,
        endAt: dayjs(initialValue?.startAt, "DD/MM/YYYY HH:mm:ss")
          .set("hour", values?.endAt.hour())
          .set("minute", values?.endAt.minute())
          .set("second", 0)
          .format("YYYY-MM-DD HH:mm"),
      };

      if (dataToPost?.id) {
        const res = await ShiftingOperationsService.updateShiftingOperations(
          initialValue?.id,
          dataToPost
        );
        if (
          res?.status === HttpStatusCode.Ok ||
          res?.status === HttpStatusCode.Created
        ) {
          onFinish();
          form.resetFields();
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoadingSubmit(false);
    }
  };

  const showReferenceChaland = () => {
    setIsChecked((prev: boolean) => !prev);
  };

  return (
    <div>
      <FormProvider
        Form={Form}
        form={form}
        debug={true}
        onFinish={(values: any) => {
          onSubmitHandler(values);
        }}
        onFinishFailed={(err) => {
          console.log("err", err);
        }}
      >
        <Loader isLoading={isLoading} />
        <div className="d-none">
          <SimpleInput
            Form={Form}
            label="ID"
            name={"id"}
            customInput={<Input />}
          />
        </div>
        <div className={`${style.form__input_label}`}>Informations</div>
        <div className={`${style.form} ${style.container} mt-2`}>
          <div className={style.form__item_wrapper}>
            {/* Ligne 1 */}
            <div className={style.form__row}>
              <div className={style.form__col}>
                <SimpleInput
                  Form={Form}
                  label="Marchandise"
                  name="loadType"
                  rules={[{ required: true, message: "Ce champ est requis" }]}
                  customInput={
                    <InputSelect
                      uri={API_URLs.operations.shifting.select.loadTypeSelect}
                      onChange={(e) => {
                        console.log("Selected load state:", e);
                      }}
                    />
                  }
                />
              </div>
              <div className={style.form__col}>
                <SimpleInput
                  Form={Form}
                  label="Type de shifting"
                  name="shiftingType"
                  customInput={
                    <InputSelect
                      uri={
                        API_URLs.operations.shifting.select.shiftingTypeSelect
                      }
                      onChange={(e) => {
                        console.log("Selected load state:", e);
                      }}
                    />
                  }
                />
              </div>
            </div>

            {/* Ligne 2 */}
            <div className={style.form__row}>
              <div className={style.form__col}>
                <SimpleInput
                  Form={Form}
                  label="Identifiant"
                  name="uniqueIdentifier"
                  customInput={<Input />}
                />
              </div>
              <div className={style.form__col}>
                <SimpleInput
                  Form={Form}
                  label="Tonnage (T)"
                  name="tonnage"
                  rules={[{ required: true, message: "Ce champ est requis" }]}
                  customInput={<Input onKeyDown={handleNumericDecimalInput}/>}
                />
              </div>
            </div>

            {/* Ligne 3 */}
            <div className={style.form__row}>
              <div className={style.form__col}>
                <SimpleInput
                  Form={Form}
                  label="État"
                  name="loadFillingStatus"
                  rules={[{ required: true, message: "Ce champ est requis" }]}
                  customInput={
                    <InputSelect
                      uri={
                        API_URLs.operations.shifting.select
                          .loadFillingStatusSelect
                      }
                      onChange={(e) => {
                        console.log("Selected load state:", e);
                      }}
                    />
                  }
                />
              </div>
              <div className={`${style.form__col} ${style.qtyField}`}>
                <SimpleInput
                  Form={Form}
                  label="Nature"
                  name="loadUnit"
                  customInput={<Input />}
                />
              </div>
            </div>

            {/* Ligne 4 */}
            <div className={style.form__row}>
              <div className={style.form__col}>
                <SimpleInput
                  Form={Form}
                  label="Type"
                  name="loadProperty"
                  rules={[{ required: true, message: "Ce champ est requis" }]}
                  customInput={<Input />}
                />
              </div>
              <div className={`${style.form__col} ${style.form__time_picker}`}>
                <SimpleInput
                  Form={Form}
                  label="Heure de fin"
                  name={"endAt"}
                  rules={[
                    {
                      required: true,
                      message: `Ce champ est requis`,
                    },
                  ]}
                  customInput={
                    <TimePicker
                      style={{ width: "100%" }}
                      needConfirm={false}
                      format={"HH:mm"}
                      showNow={false}
                    />
                  }
                />
              </div>
            </div>

            {/* Ligne 5*/}
            <div className={style.form__row}>
              <div className={style.form__col}>
                <SimpleInput
                  Form={Form}
                  label="Chaland"
                  name="isBargeUsed"
                  customInput={
                    <Switch
                      checkedChildren="OUI"
                      unCheckedChildren="NON"
                      checked={isChecked}
                      onChange={showReferenceChaland}
                    />
                  }
                />
              </div>
              {isChecked && (
                <div className={`${style.form__col}`}>
                  <SimpleInput
                    Form={Form}
                    label="Référence chaland"
                    name="bargeMaterial"
                    rules={[{ required: true, message: "Ce champ est requis" }]}
                    customInput={
                      <InputSelect
                        uri={
                          API_URLs.operations.shifting.select
                            .bargeMaterialSelect
                        }
                        onChange={(e) => {
                          console.log("Selected load state:", e);
                        }}
                        valueOption={{
                          labelKey: "reference",
                          valueKey: "@id",
                        }}
                      />
                    }
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        <div className={`${style.form__input_label} mt-5`}>Progression</div>
        <div className={`${style.container__progression} mt-2`}>
          <ShiftingStepsEditor
            initialValue={initialValue}
            onChange={setShiftingSteps}
          />
        </div>

        <div className={style.form__action_wrapper}>
          <CustomButton
            type="submit"
            className={style.form__action_button}
            isLoading={isLoadingSubmit}
          >
            Enregistrer
          </CustomButton>
        </div>
      </FormProvider>
    </div>
  );
};

export default ShiftingOperationsForm;
